<template>
  <div class="admin-login">
    <!-- Background Image -->
    <div class="background-image" :style="bgStyle"></div>
    
    <!-- Language Switcher in Top Right -->
    <div class="language-switcher-position">
      <LanguageSwitcher @language-changed="handleLanguageChange" />
    </div>
    
    <!-- Content -->
    <div class="login-container">
      <div
        class="login-form"
        :class="[
          $i18n.locale === 'ar' ? 'arabic-form' : 'english-form'
        ]"
      >

        <img 
          src="../assets/logo.png" 
          alt="Tuscany House Logo" 
          class="w-16 h-16 sm:w-20 sm:h-20 rounded-full mb-4 cursor-pointer hover:opacity-80 transition-opacity" 
          @click="goToHome"
        />
        <h1 class="text-xl sm:text-2xl font-bold text-[#F5BE32] mb-6 sm:mb-8 tracking-wide text-center">{{ $t('admin.login.title') }}</h1>
        <form @submit.prevent="handleLogin" class="w-full flex flex-col gap-4 sm:gap-6">
          <div>
            <label class="block text-white mb-2 font-medium text-sm sm:text-base" for="username">{{ $t('admin.login.username') }}</label>
            <input v-model="username" id="username" type="text" class="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-[#4F4F4F] text-white border border-[#4F4F4F] focus:border-[#F5BE32] outline-none transition text-sm sm:text-base" :placeholder="$t('admin.login.usernamePlaceholder')" />
          </div>
          <div>
            <label class="block text-white mb-2 font-medium text-sm sm:text-base" for="password">{{ $t('admin.login.password') }}</label>
            <input v-model="password" id="password" type="password" class="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-[#4F4F4F] text-white border border-[#4F4F4F] focus:border-[#F5BE32] outline-none transition text-sm sm:text-base" :placeholder="$t('admin.login.passwordPlaceholder')" />
          </div>
          <button type="submit" class="w-full bg-[#F5BE32] text-[#1F1F1F] font-semibold py-2 sm:py-3 rounded-lg mt-2 hover:bg-[#e5ae22] transition-colors text-sm sm:text-base">{{ $t('admin.login.loginButton') }}</button>
          <div v-if="errorMessage" class="text-red-500 text-center text-xs sm:text-sm mt-2">{{ errorMessage }}</div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import adminBg from '@/assets/Admin/adminbg.png'
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'

export default {
  name: 'AdminLogin',
  components: {
    LanguageSwitcher
  },
  data() {
    return {
      username: "",
      password: "",
      errorMessage: "",
    };
  },
  computed: {
    bgStyle() {
      return {
        backgroundImage: `url(${adminBg})`,
      };
    },
  },
  methods: {
    goToHome() {
      this.$router.push('/')
    },
    handleLanguageChange(lang) {
      console.log('Language changed to:', lang)
    },
    async handleLogin() {
      // Clear any previous error messages
      this.errorMessage = ''
      
      // Check if fields are empty
      if (!this.username.trim() || !this.password.trim()) {
        this.errorMessage = this.$t('admin.login.errorEmpty')
        return
      }
      
      // Check if username contains 'admin' or 'chef'
      const username = this.username.toLowerCase()
      if (!username.includes('admin') && !username.includes('chef')) {
        this.errorMessage = this.$t('admin.login.errorInvalidUsername')
        return
      }
      
      try {
        // Query Firebase adminUsers collection
        const db = getFirestore();
        const adminUsersRef = collection(db, "adminUsers");
        const q = query(
          adminUsersRef,
          where("username", "==", this.username),
          where("password", "==", this.password)
        );

        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const userDoc = querySnapshot.docs[0];
          const userData = userDoc.data();

          // Store admin session
          localStorage.setItem("adminLoggedIn", "true");
          localStorage.setItem("userType", userData.role || "admin");

          // Redirect based on username
          if (username.includes("admin")) {
            this.$router.push("/admin");
          } else if (username.includes("chef")) {
            this.$router.push("/chef/dashboard");
          }
        } else {
          this.errorMessage = this.$t('admin.login.errorInvalidCredentials')
        }
      } catch (error) {
        console.error('Login error:', error)
        this.errorMessage = this.$t('admin.login.errorInvalidCredentials')
      }
    }
  }
}
</script>

<style scoped>
.admin-login {
  position: relative;
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.background-image {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 1;
}

.language-switcher-position {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 20;
}

.login-container {
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0 1rem;
}

.login-form {
  background-color: #1F1F1F;
  border-radius: 1rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 4px solid #F5BE32;
}

.english-form {
  max-width: 24rem;
  padding-left: 2.5rem;
  padding-right: 2.5rem;
}

.arabic-form {
  max-width: 32rem;
  padding-left: 3rem;
  padding-right: 3rem;
}

@media (min-width: 640px) {
  .english-form {
    max-width: 28rem;
    padding: 2rem 3rem;
  }
  
  .arabic-form {
    max-width: 36rem;
    padding: 2rem 3rem;
  }
}

@media (min-width: 1024px) {
  .english-form {
    max-width: 32rem;
    padding: 2.5rem 4rem;
  }
  
  .arabic-form {
    max-width: 40rem;
    padding: 2.5rem 4rem;
  }
}

/* RTL Support for Admin Login */
[dir="rtl"] .language-switcher-position {
  right: auto;
  left: 1rem;
}

[dir="rtl"] .admin-login {
  text-align: right;
}

[dir="rtl"] .text-center {
  text-align: center;
}

[dir="rtl"] .flex {
  flex-direction: row;
}

[dir="rtl"] .flex-col {
  flex-direction: column;
}

[dir="rtl"] .items-center {
  align-items: center;
}

[dir="rtl"] .justify-center {
  justify-content: center;
}
</style> 